/**
 * The box page's default copy (the rule is in `shared.ts`): the breadcrumb over the box, the words
 * around its name and price, the lines around the opener, and the contents' heading, sorts and
 * count. The opener's controls and refusals are the platform's, and so is the disclosure of a
 * limited entry (`dropTableDisclosure.ts`).
 */
import { capitalizeTerm, type StorefrontTerminology, termCount } from "../storefrontTerminology.js";
import { FORGE_CREATOR_LABEL } from "./forge.js";
import { STOREFRONT_FRAME_COPY } from "./frame.js";
import { buyCurrencyLabel } from "./shared.js";

/** The contents' sorts: by value, or by chance (the design system's `DropSort`). */
export type BoxContentsSort = "value" | "odds";

/** How the opens beside the price are counted: 1,204. */
const NUMBER = new Intl.NumberFormat("en-US");

/** A box's page's words, by section, in the store's terms and currency. */
export interface BoxPageCopy {
  /** The pages above the box, and the box's place in the trail until its name loads. */
  breadcrumb: { home: string; boxes: string; loading: string };
  heading: {
    /** Beside the price: what it buys, and how often the box was opened when that is known. */
    priceNote: (openedCount?: number) => string;
    /** Before a forge box's creator's name. */
    creatorLabel: string;
    /** Signed in with battles on: the link to start a battle with this box. */
    battleWithBox: string;
  };
  opener: {
    /** Signed out, opens are demos: what that means, and the sign-in link's label. */
    demo: { message: string; signIn: string };
    /** Under the opener where the store sells its currency: the question, and the link's label. */
    footnote: { short: string; buy: string };
    /** The last line of the opener's idle view: the claim, and the fairness page link's label. */
    fairness: { statement: string; link: string };
  };
  contents: {
    title: string;
    /** The sort group's accessible name, and each sort's label. */
    sortLabel: string;
    sorts: Record<BoxContentsSort, string>;
    /** Beside the title: how many entries can drop. */
    count: (entries: number) => string;
  };
}

export function defaultBoxPageCopy({
  terms,
  currencyPlural,
}: {
  terms: StorefrontTerminology;
  /** The currency's plural name. */
  currencyPlural: string;
}): BoxPageCopy {
  const { box, item, battle } = terms;
  return {
    breadcrumb: {
      home: STOREFRONT_FRAME_COPY.home,
      boxes: capitalizeTerm(box.plural),
      loading: capitalizeTerm(box.singular),
    },
    heading: {
      priceNote: (openedCount) =>
        `per open${openedCount !== undefined ? ` · ${NUMBER.format(openedCount)} opened` : ""}`,
      creatorLabel: FORGE_CREATOR_LABEL,
      battleWithBox: `${capitalizeTerm(battle.singular)} with this ${box.singular}`,
    },
    opener: {
      demo: {
        message:
          "You're in demo mode — spins roll locally with the real odds, but nothing persists.",
        signIn: "Sign in to open for real",
      },
      footnote: { short: `Short on ${currencyPlural}?`, buy: buyCurrencyLabel(currencyPlural) },
      fairness: { statement: "Provably fair", link: "How it works" },
    },
    contents: {
      title: "What's inside",
      sortLabel: `Sort ${item.plural}`,
      sorts: { value: "Highest value", odds: "Best odds" },
      count: (entries) => termCount(entries, item),
    },
  };
}
